import React, { useMemo, useRef, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import Textfield from "@atlaskit/textfield";
import styled from "styled-components";
import ReactQuill from "react-quill";

import "react-quill/dist/quill.bubble.css";
import "./css/react-quill.scss";

const HexIcon = styled.span`
  font-size: 16px;
  margin-left: 6px;
`;

const DEFAULT_TEXT_COLORS = [
  "#0052CC",
  "#172b4d",
  "#00a2e0",
  "#00badd",
  "#00b779",
  "#ffc300",
  "#FF8B00",
  "#ff4310",
  "#6a4ec7",
  "#DFE1E6",
  "#ffffff",
  "custom-color",
];

const DEFAULT_HIGHLIGHT_COLORS = [
  "#ff43aa",
  "#ff7fae",
  "#f7b755",
  "#fce01a",
  "#fcfd51",
  "#e6fd6c",
  "#d8fd51",
  "#91fc51",
  "#aefefe",
  "#6ee4fe",
  "#c697fc",
  "transparent",
  "custom-color",
];

const Editor = ({
  id,
  placeholder,
  text,
  setText,
  textColors,
  highlightColors,
}) => {
  const editorRef = useRef();
  const textfieldRefs = [useRef(), useRef()];

  let customColor = "";

  useEffect(() => {
    const colorPickers =
      editorRef.current.querySelectorAll(`.ql-picker-options`);

    [...colorPickers].forEach((el, i) => {
      const div = document.createElement("div");

      div.style.float = "left";
      div.style.width = "80%";

      el.append(div);

      ReactDOM.render(
        <Textfield
          ref={textfieldRefs[i]}
          isCompact
          placeholder="hex color"
          elemBeforeInput={<HexIcon>#</HexIcon>}
          onChange={(e) => {
            const value = `#${e.target.value.replace(/#/g, "")}`;
            customColor = value;
          }}
        />,
        div
      );
    });
  }, []);

  const resetColorInputValue = () => {
    if (textfieldRefs[0]?.current?.value) {
      textfieldRefs[0].current.value = "";
    }
    if (textfieldRefs[1]?.current?.value) {
      textfieldRefs[1].current.value = "";
    }
  };

  const modulesQuill = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline"],
          [
            typeof textColors !== "undefined"
              ? {
                  color: textColors?.length ? textColors : DEFAULT_TEXT_COLORS,
                }
              : undefined,
            {
              background: highlightColors?.length
                ? highlightColors
                : DEFAULT_HIGHLIGHT_COLORS,
            },
          ],
          ["link"],
        ],
        handlers: {
          color(value) {
            const color = value === "custom-color" ? customColor : value;
            this.quill.format("color", color);

            resetColorInputValue();
          },
          background(value) {
            const color = value === "custom-color" ? customColor : value;
            this.quill.format("background", color);

            resetColorInputValue();
          },
        },
      },
      clipboard: {
        matchers: [
          [
            Node.ELEMENT_NODE,
            (node, delta) => {
              const ops = [];
              delta.ops.forEach((op) => {
                if (op.insert && typeof op.insert === "string") {
                  ops.push({
                    insert: op.insert,
                  });
                }
              });
              delta.ops = ops;
              return delta;
            },
          ],
        ],
      },
    }),
    []
  );

  return (
    <div role="none" style={{ width: "100%" }}>
      <div ref={editorRef}>
        <ReactQuill
          id={id}
          theme="bubble"
          placeholder={placeholder}
          value={text}
          onChange={(value) => setText(value)}
          modules={modulesQuill}
        />
      </div>
    </div>
  );
};

function QuillEditor({
  id,
  placeholder = "",
  text,
  setText,
  enableColors,
  textColors = undefined,
  highlightColors = undefined,
}) {
  const [editorKey, setEditorKey] = useState("editor-key");
  const [fontColors, setFontColors] = useState([]);
  const [backgroundColors, setBackgroundColors] = useState([]);

  useEffect(() => {
    if (enableColors) {
      if (textColors?.length) {
        setFontColors([...textColors]);
      } else {
        setFontColors([]);
      }
    } else {
      setFontColors(undefined);
    }

    if (highlightColors?.length) {
      setBackgroundColors([...highlightColors]);
    } else {
      setBackgroundColors([]);
    }

    setEditorKey(crypto.randomUUID());
  }, [enableColors, textColors, highlightColors]);

  return (
    <Editor
      key={editorKey}
      id={id}
      placeholder={placeholder}
      text={DOMPurify.sanitize(text)}
      setText={(value) => setText(DOMPurify.sanitize(value))}
      textColors={fontColors}
      highlightColors={backgroundColors}
    />
  );
}

QuillEditor.defaultProps = {
  id: null,
  placeholder: "",
  textColors: [],
  highlightColors: [],
  enableColors: true,
};

QuillEditor.propTypes = {
  id: PropTypes.string,
  placeholder: PropTypes.string,
  setText: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  textColors: PropTypes.array,
  highlightColors: PropTypes.array,
  enableColors: PropTypes.bool,
};

export default QuillEditor;
